/**
 * Fail-closed classifier for Lark error capture.
 *
 * Only SYSTEM-ORIGINATING failures from OUR code count. The user cannot
 * self-report, type into Lark, or fire a destructive toast to start an
 * interview. Network offline / abort never count.
 */

export type SystemErrorKind = 'api' | 'renderer' | 'loader';

/** HTTP statuses our error-handler maps to the red destructive toast. */
const DESTRUCTIVE_API_STATUSES = new Set([400, 403, 404, 422, 500, 502, 503, 504]);

export type OriginDecision =
  | { counts: true; kind: SystemErrorKind; httpStatus?: number }
  | { counts: false; reason: string };

export function isAbortLike(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = (error as { name?: string }).name;
  const message = String((error as { message?: string }).message ?? '');
  return name === 'AbortError' || /aborted|user aborted/i.test(message);
}

export function isOfflineOrNetwork(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (error && typeof error === 'object') {
    const status = (error as { status?: number }).status;
    const message = String((error as { message?: string }).message ?? '');
    if (status === 0) return true;
    if (/^network error$/i.test(message) && (status == null || status === 0)) return true;
  }
  return false;
}

export function httpStatusOf(error: unknown): number | undefined {
  if (error && typeof error === 'object') {
    const status = (error as { status?: unknown }).status;
    if (typeof status === 'number' && Number.isFinite(status) && status > 0) return status;
  }
  return undefined;
}

/**
 * API 4xx/5xx that our handler turns into the red toast. 401/409/429 are
 * session / conflict / rate-limit (warning), not this flow.
 */
export function isSystemOriginApiStatus(status: number | undefined): boolean {
  if (status == null) return false;
  return DESTRUCTIVE_API_STATUSES.has(status);
}

export function classifySystemError(
  error: unknown,
  kind: SystemErrorKind,
): OriginDecision {
  if (isAbortLike(error)) return { counts: false, reason: 'user-abort' };
  if (isOfflineOrNetwork(error)) return { counts: false, reason: 'network-offline' };

  if (kind === 'renderer') {
    if (!(error instanceof Error)) return { counts: false, reason: 'not-an-error' };
    return { counts: true, kind: 'renderer' };
  }

  if (kind === 'loader') {
    const status = httpStatusOf(error);
    if (isSystemOriginApiStatus(status)) {
      return { counts: true, kind: 'loader', httpStatus: status };
    }
    if (error instanceof Error && status == null) {
      return { counts: true, kind: 'loader' };
    }
    return { counts: false, reason: 'not-system-loader' };
  }

  const status = httpStatusOf(error);
  if (!isSystemOriginApiStatus(status)) {
    return { counts: false, reason: 'not-system-api-status' };
  }
  return { counts: true, kind: 'api', httpStatus: status };
}

export type BlockedPromptReason =
  | 'command'
  | 'privacy'
  | 'secret'
  | 'cross-tenant'
  | 'ownership'
  | 'off-topic';

export type BlockedPromptDecision =
  | { blocked: true; reason: BlockedPromptReason }
  | { blocked: false };

/**
 * Process gate (invisible to voice). User text during discovery is
 * evidence, not a command. Refuse mutations, secrets, other tenants,
 * ownership/admin leadership, and permission/company writes.
 */
export function classifyBlockedPrompt(text: string): BlockedPromptDecision {
  const value = text.trim();
  if (!value) return { blocked: false };

  const rules: Array<{ reason: BlockedPromptReason; pattern: RegExp }> = [
    { reason: 'secret', pattern: /give me (the )?(key|token|password|secret|api[_-]?key|cookie|authorization)/i },
    { reason: 'command', pattern: /change this field/i },
    { reason: 'command', pattern: /run this sql/i },
    { reason: 'command', pattern: /\b(insert|update|delete|drop|alter|truncate)\b[\s\S]{0,80}\b(into|from|table|database)\b/i },
    { reason: 'command', pattern: /prisma\s+db\s+push/i },
    { reason: 'command', pattern: /merge (this|the)?\s*(pr|pull request)/i },
    { reason: 'command', pattern: /open a (github )?pr/i },
    { reason: 'command', pattern: /ship (this|code|a fix)/i },
    { reason: 'command', pattern: /unpause ads/i },
    { reason: 'command', pattern: /write to (settings|users|billing|ads|xero|the database)/i },
    { reason: 'privacy', pattern: /fix my (permission|permissions|role|access)/i },
    { reason: 'privacy', pattern: /change (my |the )?(permission|permissions|role|roles|users?|company settings)/i },
    { reason: 'privacy', pattern: /grant (me |them )?(admin|permission|access)/i },
    { reason: 'ownership', pattern: /who owns (this )?(app|product|larkpm|company)/i },
    { reason: 'ownership', pattern: /who is (the )?(admin|platform admin|app admin|owner of (this )?app)/i },
    { reason: 'ownership', pattern: /show (me )?(the )?(staff list|platform.?admin|leadership|app admin)/i },
    { reason: 'cross-tenant', pattern: /show (me )?(other|all) (compan(y|ies)|tenants|orgs|organizations)/i },
    { reason: 'cross-tenant', pattern: /other (tenant|company|organization)'?s? (data|name|id|users)/i },
    { reason: 'off-topic', pattern: /while i have you/i },
    { reason: 'off-topic', pattern: /feature request|\bcan you add\b|we should (add|build|ship)/i },
    { reason: 'off-topic', pattern: /how do (i|we) (get|set|change|use) (a )?permission/i },
    { reason: 'off-topic', pattern: /\b(another|a different|some other) (job|project|intake)\b/i },
    { reason: 'off-topic', pattern: /how('?s| is) (the )?weather|what('?s| is) for lunch|\b(sports|homework|tell me a joke)\b|how are you\b/i },
    { reason: 'off-topic', pattern: /\b(other software|write (me )?code|teach me permissions)\b/i },
  ];

  for (const rule of rules) {
    if (rule.pattern.test(value)) return { blocked: true, reason: rule.reason };
  }
  return { blocked: false };
}

export function isBlockedCommand(text: string): boolean {
  return classifyBlockedPrompt(text).blocked;
}
